import { ItemCard, ItemCardContainer } from "@/components/ItemCard";
import { ListContainer } from "@/components/layout/ListContainer";
import { useModals } from "@/features/modals/hooks";
import { NotFoundOrderItem } from "@/features/orders/components/NotFoundOrderItem";
import { OrderCardDetails } from "@/features/orders/components/OrderCardDetails";
import { onDeleteOrder } from "@/features/orders/utils";
import { useActiveProject } from "@/features/project/ActiveProject";
import { setActiveProject } from "@/features/project/setActiveProject";
import { currencyFormatter } from "@/utils/currencyFormatter";
import { getOrderImage } from "@/utils/getItemImage";
import {
  getAllDraftOrdersIds,
  mapApiResponseToOrders,
  useDraftOrder,
} from "@repo/api";
import {
  Skeleton,
  Text,
  crossIcon,
  pencilIcon,
  shoppingBagIcon,
} from "@repo/skapa";
import { getLanguageLocal, t } from "@repo/translation";
import { useSettings, useToast } from "@repo/ui";
import { hasItems, trackAnalytics } from "@repo/utils";

interface DraftOrderItemProps {
  opportunityId?: number;
  draftOrderId: string;
  editable?: boolean;
}
const DraftOrderItem = ({
  opportunityId,
  draftOrderId,
  editable,
}: DraftOrderItemProps) => {
  const { pushModal } = useModals();
  const { updateToast } = useToast();
  const { market, language, general } = useSettings();
  const { opportunity } = useActiveProject();
  const { data: draftOrderFromApi, isValidating } = useDraftOrder(draftOrderId);
  const draftOrderData = mapApiResponseToOrders(
    draftOrderFromApi,
    draftOrderId
  );
  const draftOrder = draftOrderData[0];

  const openOrderInSalja = {
    onAddToBag: (e: any) => {
      e.preventDefault();
      e.stopPropagation();

      if (opportunity) {
        setActiveProject(opportunity);
      }

      const link = `${
        import.meta.env.VITE__WEB_SALJA_V2
      }/${market}/${language}/orders/view/draft/${draftOrderId}`;

      window.open(link, "_blank");
    },
    icon: shoppingBagIcon,
    disabled: !draftOrderId,
    loading: false,
  };

  const viewAttachments = () => {
    pushModal({
      type: "DRAFT_ORDER",
      draftOrderId,
      activeTab: "order-attachments",
    });
  };

  const onConfirmDisassociate = () => {
    pushModal({
      type: "CONFIRM",

      onConfirm: () =>
        onDeleteOrder({
          opportunityId,
          orderId: draftOrderId,
          updateToast,
        }),
      title: t("action.remove_from_project"),
      message: t("prompt.confirm_remove_draft_order"),
    });
  };

  const menuItems = [
    {
      icon: pencilIcon,
      title: t("action.view_attachments"),
      onClick: () => {
        viewAttachments();
        trackAnalytics("open_order_attachments");
      },
    },
    {
      icon: crossIcon,
      title: t("action.remove_from_project"),
      onClick: () => {
        onConfirmDisassociate();
        trackAnalytics("remove_draft_order_from_project");
      },
    },
  ];

  if (isValidating && !draftOrder) {
    return (
      <ItemCardContainer>
        <Skeleton height="115px" width="100%" />
      </ItemCardContainer>
    );
  }

  if (!draftOrder?.orderNo) {
    return (
      <NotFoundOrderItem
        key={draftOrderId}
        orderId={draftOrderId}
        opportunityId={opportunity?.id}
      />
    );
  }

  return (
    <ItemCard
      editable={editable}
      menuItems={menuItems}
      onClick={() => {
        if (editable) {
          pushModal({
            type: "DRAFT_ORDER",
            draftOrderId,
          });
        }
      }}
      image={getOrderImage(draftOrder)}
      amount={currencyFormatter({
        value: draftOrder.orderValue ?? 0,
        currency: draftOrder.currencyCode,
        locale: getLanguageLocal(language, market),
        showSign: true,
        disableDecimals: Boolean(general?.localization?.disable_decimals),
        fractions: 2,
      })}
      details={<OrderCardDetails order={draftOrder} />}
      actions={[
        {
          id: `add-to-cart-button__draft-${draftOrder.orderNo}`,
          text: `${t("action.open_order_in_salja")} - ${draftOrder.orderNo}`,
          title: t("action.open_order_in_salja"),
          icon: shoppingBagIcon,
          iconOnly: true,
          disabled: openOrderInSalja.disabled,
          loading: openOrderInSalja.loading,
          onClick: openOrderInSalja.onAddToBag,
          type: "emphasised",
        },
      ]}
    />
  );
};

interface DraftOrdersProps {
  editable?: boolean;
}

export const DraftOrders = ({ editable }: DraftOrdersProps) => {
  const { opportunity } = useActiveProject();

  const draftOrderIds = getAllDraftOrdersIds(opportunity);

  if (!hasItems(draftOrderIds)) {
    return <Text>{t("prompt.no_draft_orders")}</Text>;
  }

  return (
    <div>
      <ListContainer>
        {draftOrderIds?.map((draftOrderId) => (
          <DraftOrderItem
            key={draftOrderId}
            opportunityId={opportunity?.id}
            draftOrderId={draftOrderId}
            editable={editable}
          />
        ))}
      </ListContainer>
    </div>
  );
};
